import { Button } from 'src/components/Button';
import { formatTimeLabel } from 'src/utils/date';
import { WorkflowTimezone } from 'src/interfaces/workflows.interface';
import {
  formatWorkflowTimezone,
  getEffectiveTimeZone,
} from 'src/utils/contactWindowUtils';

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tues', 'Wed', 'Thur', 'Fri', 'Sat'];

type SchedulePreviewProps = {
  days: number[];
  startTime: string;
  endTime: string;
  timeZone: WorkflowTimezone;
  fallbackTimeZone?: WorkflowTimezone | null;
  onEditClick?: () => void;
  isReadOnly?: boolean;
};

export function SchedulePreview({
  days,
  startTime,
  endTime,
  timeZone,
  fallbackTimeZone,
  onEditClick,
  isReadOnly = false,
}: SchedulePreviewProps) {
  const normalizedDays = Array.from(new Set(days))
    .filter((d) => Number.isInteger(d) && d >= 0 && d <= 6)
    .sort((a, b) => a - b);

  const daysLabel = normalizedDays.length
    ? normalizedDays.map((d) => WEEKDAY_LABELS[d]).join(', ')
    : 'No days in schedule';

  const startTimeLabel = formatTimeLabel(startTime);
  const endTimeLabel = formatTimeLabel(endTime);

  const effectiveTimeZone = getEffectiveTimeZone({
    primaryTz: timeZone,
    fallbackTz: fallbackTimeZone,
  });

  const timeZoneLabel = effectiveTimeZone
    ? formatWorkflowTimezone(effectiveTimeZone, { condensed: true })
    : '-';

  return (
    <div className="mt-3 rounded border bg-neutral-0 p-3 text-sm text-neutral-700">
      <div className="leading-5">
        <span className="font-medium">
          {daysLabel} • {startTimeLabel} - {endTimeLabel}
        </span>{' '}
        <span>({timeZoneLabel})</span>
      </div>
      {normalizedDays.length === 0 && (
        <div className="mt-1 text-sm text-warning-500">
          This schedule won’t run until at least one day is selected.
        </div>
      )}
      {onEditClick && !isReadOnly && (
        <div>
          <Button
            variant="text"
            size="small"
            className="p-0"
            onClick={onEditClick}
          >
            Edit campaign schedule
          </Button>
        </div>
      )}
    </div>
  );
}
